// Reception dashboard — wire-format mapper (RON-5469).
//
// Same split as the Visitor module: `ReceptionAPI.jsx` owns *which endpoint*, this file owns
// *what shape*. The API speaks snake_case with nested objects; every screen here reads a flat
// camelCase row, so a backend rename costs one line and a test fixture rather than reaching a
// component.
//
// Contract (READ-ONLY — the Laravel team's folder, never write to it):
//   D:\laragon\www\laravel-migration\docs\RON-5460\RON-5460_frontend-reception.md
//
// The unit-test fixtures are copied verbatim out of that document, so a change on their side
// fails a test here rather than a screen at a front desk.

import { DEFAULT_PER_PAGE, MAX_PER_PAGE, RECEPTION_FILTERS } from "./receptionConstants.js";

// Only keys the caller actually set.
const omitUndefined = (object) =>
  Object.entries(object).reduce((kept, [key, value]) => {
    if (value !== undefined) kept[key] = value;
    return kept;
  }, {});

// `HH:MM:SS` in, `HH:MM` on screen. Also tolerates a full datetime, because `checked_in_at` and
// `expected_out` are datetimes while `arrival_time` is a bare time.
const TIME_ONLY = /^\d{1,2}:\d{2}$/;

// ── THE `_local` TWINS (API-STATUS §0, 13-October-2026) ──────────────────────────────────────
//
// > Every date or time field `X` in a covered response has a twin `X_local` beside it.
// > Clients always read `X_local` and never convert anything.
//
// **The reception board is a covered response.** For a row tied to a site, "local" is THAT SITE's
// zone, row by row — so one board can hold Dublin and Sydney and each row reads in its own time.
//
// WHY THIS MATTERS HERE RATHER THAN BEING TIDINESS. Two fields on this screen are INSTANTS,
// stored in UTC: `checked_in_at` and `checked_out_at`. We never converted them — this mapper
// only reformats `HH:MM:SS` to `HH:MM` — so a contractor who checked in at 09:30 in Dublin was
// shown as 08:30 all summer. `arrival_time` and `expected_out` are wall clocks and were always
// right, which is exactly why the wrong ones were hard to spot: three times on the row agreed and
// the fourth did not.
//
// Reading the twin IS the fix. No conversion here, ever — the zone belongs to the row and the
// server is the only thing that knows it.
//
// The fallback is deliberate rather than defensive clutter: their note says "nothing was renamed
// or removed… your current screens keep working", so a response from before the twins shipped —
// or a field they have not covered yet — still reads instead of going blank.
const localOf = (api, key) => api?.[`${key}_local`] ?? api?.[key];

export const timeFromApi = (value) => {
  if (typeof value !== "string" || value === "") return undefined;

  const withoutDate = value.includes("T") ? value.split("T")[1] : value.split(" ").pop();
  const [hours, minutes] = String(withoutDate).split(":");
  const time = minutes === undefined ? withoutDate : `${hours}:${minutes}`;

  return TIME_ONLY.test(time) ? time : undefined;
};

// ── The board query (§3) ─────────────────────────────────────────────────────────────────────
//
// `site_id` is required on EVERY call (§2) — a board with no site is not a board, and the server
// refuses it. It is validated here so the failure is a readable error in development rather than
// a 422 at a front desk.
//
// `filter`, `q` and `date` COMBINE. They are three narrowing conditions on one query, not three
// passes — the doc calls out a shipped bug elsewhere where two filter passes fought each other.
export const boardQueryToApi = ({ SiteID, Date: date, Filter, Query, Page, PerPage } = {}) => {
  if (!SiteID) {
    throw new Error("Reception board requires a site_id — every reception call is scoped to one site.");
  }

  if (Filter != null && Filter !== "" && !RECEPTION_FILTERS.includes(Filter)) {
    // Refused rather than dropped. A dropped filter renders an unfiltered board that looks
    // filtered, and "0 expected" reads to a receptionist as a fact about their day.
    throw new Error(
      `Unknown reception filter "${Filter}". Use one of: ${RECEPTION_FILTERS.join(", ")}.`
    );
  }

  if (PerPage != null && (Number(PerPage) < 1 || Number(PerPage) > MAX_PER_PAGE)) {
    // The server refuses these too (422, never a silent correction). Catching it here keeps the
    // reason in words rather than as a status code.
    throw new Error(`per_page must be between 1 and ${MAX_PER_PAGE}; got ${PerPage}.`);
  }

  if (Page != null && Number(Page) < 1) {
    throw new Error(`page is 1-based; got ${Page}.`);
  }

  return omitUndefined({
    site_id: SiteID,
    date: date || undefined,
    filter: Filter || undefined,
    q: Query || undefined,
    page: Page ?? undefined,
    per_page: PerPage ?? undefined,
  });
};

// ── The walk-in body (§5) ────────────────────────────────────────────────────────────────────
//
// IT REGISTERS AND CHECKS THEM IN, IN ONE CALL. "A walk-in is standing at the desk — creating them
// `expected` and making the receptionist press a second button would misreport who is in the
// building for as long as it took. Don't build a second step."
//
// `name` and `host_id` are required: "somebody at a desk is there to see a person, and a visit
// with no host is one nobody can be told about or escort."
export const walkInToApi = ({
  SiteID,
  Name,
  HostID,
  Company,
  Email,
  Purpose,
  ExpectedOut,
  Date: date,
  IsVip,
  BuildingID,
  RequireHs,
  RequireNda,
} = {}) => {
  if (!SiteID) throw new Error("A walk-in needs a site_id.");
  if (!String(Name || "").trim()) throw new Error("A walk-in needs a name.");
  if (!HostID) throw new Error("A walk-in needs a host_id.");

  return omitUndefined({
    site_id: SiteID,
    name: String(Name).trim(),
    host_id: HostID,
    company: Company || undefined,
    // Optional, but validated when present — "a typo means the host is told about somebody the
    // system can never contact again".
    email: Email || undefined,
    purpose: Purpose || undefined,
    // A full datetime, unlike the invite module's bare `HH:MM`: reception stores it that way, and
    // a bare time has no meaning on a visit that could span days.
    expected_out: ExpectedOut && date ? `${date} ${ExpectedOut}:00` : undefined,
    is_vip: IsVip === undefined ? undefined : Boolean(IsVip),
    building_id: BuildingID || undefined,
    // STORED, AND READ BY NOTHING YET (A16). They record what the desk said was required, not
    // what anybody signed — see the note in `receptionConstants.js`. Sent as real booleans so the
    // intent is recorded truthfully even while nothing acts on it.
    require_hs: RequireHs === undefined ? undefined : Boolean(RequireHs),
    require_nda: RequireNda === undefined ? undefined : Boolean(RequireNda),
  });
};

// ── Sites (§2) ───────────────────────────────────────────────────────────────────────────────
//
// `GET /visitor/reception/sites` → `Records.items`. Active sites, this company only.
//
// `location` — ADDED BY THE BACKEND 29-September-2026, at our request. It was absent when this
// mapper was written, so the map and the "Get directions" link were built conditional on it and
// simply never drew. They now light up on their own, with no change here beyond this note.
//
// It is "a pair or null — never half a coordinate", and the reason matters for the guard below:
// `(float) null` is `0.0`, a real point in the Gulf of Guinea, so half a pair would render as a
// pin in the sea rather than as a visible error. Both halves are checked; neither implies the
// other.
export const sitesFromApi = (records = {}) =>
  (records.items || []).map((site) => ({
    id: site.id,
    name: site.name,
    address: site.address ?? null,
    timezone: site.timezone ?? null,
    location:
      site.location?.lat != null && site.location?.lng != null ? site.location : null,
    // A12: whether the site HAS a lock PIN — never the PIN itself, which no endpoint returns.
    // `false` means the lock screen must not offer a box nobody can satisfy.
    receptionPinSet: site.reception_pin_set === true,
  }));

// ── Site counts (§4b) ────────────────────────────────────────────────────────────────────────
//
// `GET /visitor/reception/site-counts` → `{ items: [{ id, on_site, expected }], as_at }`, the
// badge beside every site in the switcher. TWO numbers, not the board's seven, so there is only
// one place for the switcher and the board to agree.
//
//   on_site  — live: who is in that building NOW
//   expected — the rest of today: arrivals who have not come yet
//
// Keyed by id rather than left as a list, because the switcher reads it per row. That also keeps
// the one distinction the contract is explicit about: **a site with nobody in it reports zeros,
// not an absent row**, since an absent row would make the switcher draw "counts not available"
// for a building that is simply empty — a different and more alarming thing. An id that is
// genuinely missing is `undefined` here; an empty site is `{ onSite: 0, expected: 0 }`.
//
// `asAt` is carried for the same reason the board has one: a number on a screen that doubles as
// an evacuation muster should be able to say when it was true.
export const siteCountsFromApi = (records = {}) => {
  const byId = {};

  (records.items || []).forEach((entry) => {
    if (!entry?.id) return;

    byId[entry.id] = {
      onSite: entry.on_site ?? 0,
      expected: entry.expected ?? 0,
    };
  });

  return { byId, asAt: localOf(records, "as_at") ?? null };
};

// ── Counts (§3) ──────────────────────────────────────────────────────────────────────────────
//
// ONE object, feeding both the five stat tiles and the seven filter tabs, so the two regions
// cannot disagree. They describe the whole day and site and do NOT move with `filter` or `q`.
export const countsFromApi = (counts = {}) => ({
  all: counts.all ?? 0,
  expected: counts.expected ?? 0,
  onSite: counts.on_site ?? 0,
  walkIns: counts.walk_ins ?? 0,
  vip: counts.vip ?? 0,
  contractors: counts.contractors ?? 0,
  overstay: counts.overstay ?? 0,
});

// ── Compliance (§4) ──────────────────────────────────────────────────────────────────────────
//
// `hs_acknowledged` and `nda_signed` are ABSENT rather than `false` until something writes them.
// That distinction has to survive the mapper, so the keys are copied ONLY when present — spreading
// a default of `false` here would destroy the very fact the backend went out of its way to keep.
const complianceFromApi = (compliance = {}) => {
  const mapped = {
    photoCaptured: compliance.photo_captured === true,
    badgeNumber: compliance.badge_number ?? null,
    badgeState: compliance.badge_state ?? null,
  };

  if ("hs_acknowledged" in compliance) mapped.hsAcknowledged = compliance.hs_acknowledged;
  if ("nda_signed" in compliance) mapped.ndaSigned = compliance.nda_signed;

  return mapped;
};

// ── A row (§4) — the same shape in the list and the detail panel ─────────────────────────────
export const rowFromApi = (api) => {
  if (!api) return null;

  return {
    id: api.id,
    bookingCode: api.booking_code,
    name: api.name,
    company: api.company,
    email: api.email,
    kind: api.kind,
    isVip: api.is_vip === true,
    isWalkIn: api.is_walk_in === true,
    status: api.status,

    // A FLAG on a row whose status is still `checked_in` — never a status of its own.
    isOverstay: api.is_overstay === true,
    // Minutes past `expected_out`, computed server-side so every device agrees.
    overstayBy: api.overstay_by ?? null,

    host: api.host,
    building: api.building,
    // The visitor's purpose; for a contractor this carries `work_description`, so one field
    // serves both.
    purpose: api.purpose,

    startDate: api.start_date,
    endDate: api.end_date ?? api.start_date,
    // All four read the twin. `arrival_time` and `expected_out` are wall clocks, so their twin
    // is a copy and nothing changes — but reading them the same way means nobody has to remember
    // which fields are instants, which is the point of the rule.
    arrivalTime: timeFromApi(localOf(api, "arrival_time")),
    expectedOut: timeFromApi(localOf(api, "expected_out")),
    checkedInAt: timeFromApi(localOf(api, "checked_in_at")),
    checkedOutAt: timeFromApi(localOf(api, "checked_out_at")),

    // At least 1, and can be more: one contractor visit can cover a crew of six. The muster
    // counts people, not rows.
    personnel: api.personnel ?? 1,

    // `null` for a visitor and an object for a contractor — returned as null rather than omitted
    // so one shape binds.
    contractor: api.contractor
      ? {
          id: api.contractor.id,
          trade: api.contractor.trade,
          status: api.contractor.status,
          vettedUntil: api.contractor.vetted_until ?? null,
          // NOT cleared, refuse entry at the desk until an entry approval says otherwise.
          isProvisional: api.contractor.is_provisional === true,
        }
      : null,

    // `escortRequired` WAS HERE until 22-October-2026, reading `api.escort_required`. It was ours
    // — invented with the rest of the approval flow — and Phase H puts escort nowhere near a
    // board row: it is a property of the BADGE (`badge.escort_required`, from the check-in
    // reply) and a per-kiosk policy (`require_escort`). A row has no use for it.
    //
    // Removed rather than left reading false forever, which is how a field nobody sends becomes
    // a field somebody later trusts.

    compliance: complianceFromApi(api.compliance),

    // From the server. "Drive your buttons off them rather than inferring from `status` — the
    // rules include things a row cannot show you." Fail closed: absent reads as false.
    canCheckIn: api.can_check_in === true,
    canCheckOut: api.can_check_out === true,
  };
};

// ── The board (§3) ───────────────────────────────────────────────────────────────────────────
// The lock screen's two settings, carried on the board's first paint (§7b, A22).
//
// `inactivity_lock_minutes` is an ADMIN'S setting, 1–480, and the doc is blunt about it: "please
// stop hardcoding it … 30 is only what a company gets before it has saved its kiosk settings once
// — read the value rather than defaulting to it." So 30 is used only when the field is genuinely
// absent, which today means an API that has not shipped A22 yet.
//
// `reception_pin_set` also appears here, and this copy is the fresher one: since A22 a paired
// Android kiosk can change the staff PIN, so it can change without anyone touching this screen.
// Reading it off every board poll beats caching what the sites call said at login.
const DEFAULT_IDLE_LOCK_MINUTES = 30;

export const settingsFromApi = (settings) => ({
  idleLockMinutes: Number(settings?.inactivity_lock_minutes) || DEFAULT_IDLE_LOCK_MINUTES,
  // Undefined rather than false when absent, so the caller can fall back to the site's own flag
  // instead of being told there is no PIN.
  receptionPinSet:
    settings && "reception_pin_set" in settings ? settings.reception_pin_set === true : undefined,
});

// ── Entry approvals — Phase H (answers-to-frontend-18 §4) ────────────────────────────────────
//
// A LIST, not one object. Our first build carried a single `pending_approval` because two
// un-vetted contractors in a lobby at once seemed rare; the server sends an array, and two
// requests at one site is exactly the case it is built for — the kiosk can raise one while a
// receptionist is deciding another.
//
// `id` is the APPROVAL's, not the visit's. It addresses both writes, and the contract calls it
// "stable — use it to de-duplicate on refresh", which matters because the board polls.
const entryApprovalFromApi = (api = {}) => ({
  id: api.id ?? null,
  visitId: api.visit_id ?? null,
  name: api.name ?? null,
  // The server's, not `getInitials(name)`. It is what the kiosk and the badge print, so a second
  // opinion here would show one thing on screen and another on the badge.
  initials: api.initials ?? null,
  company: api.company ?? null,
  site: api.site ?? null,
  kiosk: api.kiosk ?? null,
  host: api.host ?? null,
  // ALWAYS FALSE from this endpoint — "the decision was a dashboard alert only, no email to the
  // host". Mapped anyway rather than dropped: the screen must not claim the host was told, and
  // reading the field is how that stays true if the decision is ever revisited.
  hostNotified: api.host_notified === true,
  // The `_local` twin, like every other instant on this board (API-STATUS §0).
  requestedAt: localOf(api, "requested_at") ?? null,

  // Each needs its own tick and its own id, so the objects are what the dialog reads.
  outstanding: (api.outstanding || []).map((item) => ({
    id: item.id ?? null,
    label: item.label ?? "",
    // The gap KIND — `expired`, `missing`, … — beside the sentence, so a screen can group or
    // icon them without parsing English.
    gap: item.gap ?? null,
  })),
  // The same list as plain strings. The banner summarises with these; the dialog does not.
  outstandingLabels: Array.isArray(api.outstanding_labels) ? api.outstanding_labels : [],
});

export const entryApprovalsFromApi = (records = []) =>
  (Array.isArray(records) ? records : []).map(entryApprovalFromApi);

// What approving means at this site: `off`, `reception`, or `reception_host`.
//
// Read rather than inferred from an empty list, because "nobody is waiting" and "this site does
// not allow approvals at all" are different states and only one of them changes on the next
// poll. `reception_host` behaves like `reception` for now, per Phase H.
export const UNVETTED_POLICY_DEFAULT = "reception";

export const boardFromApi = (records = {}) => ({
  settings: settingsFromApi(records.settings),
  // The server's clock at the read. Nothing is cached; this drives the "how fresh is this board"
  // line in the footer.
  asAt: localOf(records, "as_at") ?? null,
  date: records.date ?? null,
  // Returned so the TODAY pill does not have to compare clocks with the server.
  isToday: records.is_today === true,
  site: records.site ? { id: records.site.id, name: records.site.name } : null,

  counts: countsFromApi(records.counts),
  filter: records.filter ?? null,
  query: records.q ?? "",

  items: (records.items || []).map(rowFromApi),

  // PROPOSED, NOT CONTRACTED — see `MOCK_PENDING_APPROVAL`. Absent on the live board, so this
  // is null and the alert banner does not render.
  // Phase H. Pending requests only, and an empty array when there are none — so the banner
  // asks one question. `unvetted_policy` says whether approving is possible here at all.
  entryApprovals: entryApprovalsFromApi(records.entry_approvals),
  unvettedPolicy: records.unvetted_policy ?? UNVETTED_POLICY_DEFAULT,

  page: records.page ?? 1,
  perPage: records.per_page ?? DEFAULT_PER_PAGE,
  // The whole FILTERED set, not the page — it moves with filter/q/date, which is what
  // "showing 1-100 of 340 expected" needs. `counts` does not move, so the two are different on
  // purpose and both can be shown.
  total: records.total ?? 0,
  // `truncated` and `limit` are DEPRECATED and go after one release. Read this instead.
  hasMore: records.has_more === true,
});
